import { Modal, Row, Col, Button, Form, Table, Alert } from "react-bootstrap";
import { useState } from "react";
import React, {FC} from 'react';
import Loading from "../Loading";
import "./List.css";
import { store } from "./../../redux/store";
import { useAppSelector } from "../../redux/hooks";
import { getDataReader, RPWDataReader } from "../../livedataFuncs";
import { lddStateObject } from "../../redux/reducer";

export type RealtorResult = {
  name: string;
  title: string;
  officeName: string;
  officeDesignation: string;
  officeAddress: string;
  contactNumber: string;
};

type RealtorSearchModalProps = {
  show: boolean;
  onHide: () => void;
  defaultProvince?: string;
  // parent can handle adding to file
  onSelectRealtor: (r: RealtorResult) => void;
};

type SearchPhase = "search-form" | "loading" | "results";

const getProvinceString = (shortProvince?: string): string => {
  switch (shortProvince) {
    case 'ON':
      return 'Ontario';
    case 'BC':
      return 'British Columbia';
    case 'AB':
      return 'Alberta';
    case 'MB':
      return 'Manitoba';
    case 'SK':
      return 'Saskatchewan';
    case 'NS':
      return 'Nova Scotia';
    case 'NB':
      return 'New Brunswick';
    case 'NL':
      return 'Newfoundland and Labrador';
    case 'PE':
      return 'Prince Edward Island';
    case 'QC':
      return 'Quebec';
    case '':
        return 'All';
    default:
      return '';
  }
};

const getProvinceCode = (fullProvince?: string): string => {
  switch (fullProvince) {
    case 'Ontario':
      return 'ON';
    case 'British Columbia':
      return 'BC';
    case 'Alberta':
      return 'AB';
    case 'Manitoba':
      return 'MB';
    case 'Saskatchewan':
      return 'SK';
    case 'Nova Scotia':
      return 'NS';
    case 'New Brunswick':
      return 'NB';
    case 'Newfoundland and Labrador':
      return 'NL';
    case 'Prince Edward Island':
      return 'PE';
    case 'Quebec':
      return 'QC';
    case 'All':
        return '';
    default:
      return '';
  }
};

const RealtorSearchModal: FC<RealtorSearchModalProps> = ({
  show,
  onHide,
  defaultProvince,
  onSelectRealtor,
}) => {

    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const processor: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
    const LawyerID = processor.getLawyerId();
    const s = store.getState();
    const ApplicationSubCode = s.SessionInfo.ApplicationSubCode;
    
  // form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [office, setOffice] = useState("");
  const [location, setLocation] = useState("");
  const [province, setProvince] = useState(getProvinceString(defaultProvince));

  // search state
  const [phase, setPhase] = useState<SearchPhase>("search-form");
  const [results, setResults] = useState<RealtorResult[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setOffice("");
    setLocation("");
    setProvince(getProvinceString(defaultProvince));
    setResults([]);
    setErrorMsg(null);
    setPhase("search-form");
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleNewSearch = () => {
    // Default to show a new “blank” search, remove resetForm() if the previous result want to be kept
    resetForm();
  };

  const handleAddToFile = (realtor: RealtorResult) => {
      onSelectRealtor(realtor);
  };

  const handleSearch = async () => {
    setPhase("loading");
    setErrorMsg(null);

    const body = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      location: location.trim(),
      officeName: office.trim(),
      province: getProvinceCode(province),
    };

    try {
      
      const url = `/browserapi/${LawyerID}/${ApplicationSubCode}/realtor/search`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify(body),
      });

      const text = await res.text();

      let realtorInfos: RealtorResult[] = [];
      let error = "No results found. Please update your search criteria and try again.";

      if (text.includes("Server Error") || text.includes("502 Bad Gateway")) {
        error = "Server error. Please try again.";
      } else {
        const json = JSON.parse(text);
        if (json.ok) {
          realtorInfos = json.result as RealtorResult[];
        } else if (json.error === "Invalid Input") {
          error = "Valid search field required.";
        }
      }

      if (realtorInfos.length > 0) {
        setResults(realtorInfos);
        setErrorMsg(null);
      } else {
        setResults([]);
        setErrorMsg(error);
      }

      setPhase("results");
    } catch (e) {
      console.error(e);
      setResults([]);
      setErrorMsg("Server error. Please try again.");
      setPhase("results");
    }
  };

  const isLoading = phase === "loading";

  return (
    <Modal className="modal-list" show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Search Realtors.ca</Modal.Title>
      </Modal.Header>

      <div className="alert-area">
        {phase === "search-form" && (
          <Alert variant="info">
            Enter any combination of the details below and click search.
          </Alert>
        )}

        {phase === "results" && (
          errorMsg ? (
            <Alert variant="danger" className="d-flex justify-content-between align-items-center">
              <span>{errorMsg}</span>
              <Button variant="danger" size="sm" onClick={handleNewSearch}>
                New Search
              </Button>
            </Alert>
          ) : (
            <Alert variant="info" className="d-flex justify-content-between align-items-center">
              <span>Select a Realtor to add to your file or try a new search</span>
              <Button variant="primary" size="sm" onClick={handleNewSearch}>
                New Search
              </Button>
            </Alert>
          )
        )}
      </div>

      <Modal.Body style={{ position: "relative", minHeight: 200 }}>
        {/* SEARCH FORM VIEW */}
        {phase === "search-form" && (
          <>
          <div className="modal-form-content">
            <Form>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="enter information.."
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="enter information.."
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Office</Form.Label>
                    <Form.Control
                      value={office}
                      onChange={(e) => setOffice(e.target.value)}
                      placeholder="enter information.."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="enter information.."
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Province</Form.Label>
                    <Form.Select
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                    >
                      {/* province selection list */}
                      <option value="">All</option>
                      <option value="Alberta">Alberta</option>
                      <option value="British Columbia">British Columbia</option>
                      <option value="Manitoba">Manitoba</option>
                      <option value="Nova Scotia">Nova Scotia</option>
                      <option value="New Brunswick">New Brunswick</option>
                      <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                      <option value="Ontario">Ontario</option>
                      <option value="Prince Edward Island">Prince Edward Island</option>     
                      <option value="Quebec">Quebec</option>
                      <option value="Saskatchewan">Saskatchewan</option>
                      {/* ... */}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
            </div>
          </>
        )}

        {/* RESULTS / NO RESULTS VIEW */}
        {phase === "results" && (
          <>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Office</th>
                  <th>Address</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No results found.</td>
                  </tr>
                ) : (
                  results.map((r, idx) => (
                    <tr
                      key={idx}
                      className="options-container"
                      onClick={() => handleAddToFile(r)}
                    >
                      <td>{r.name}</td>
                      <td>{r.officeName}</td>
                      <td>{r.officeAddress}</td>
                      <td className="text-end">
                        <Button
                          className="hover-option"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToFile(r);
                          }}
                        >
                          Add to file
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </>
        )}

        {/* Loading overlay while searching */}
        {isLoading && (
          <Loading
            showMsg={true}
            extraMsg="Searching Realtor.ca"
            inline={true}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-primary" className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>

        {phase === "search-form" && (
          <Button variant="primary" onClick={handleSearch}>
            Search Realtor.ca
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};


export default RealtorSearchModal;